// Recap Calculation Utilities
import { getLogEntryByUserId, getRating } from '../helpers';

/**
 * Check if two dates are within one month of each other
 */
export function isWithinOneMonth(date1: string | null, date2: string): boolean {
  if (date1 == null) {
    return false;
  }
  const difference = Math.abs(
    new Date(date1).getTime() - new Date(date2).getTime(),
  );
  const oneMonthInMillis = 30 * 24 * 60 * 60 * 1000;
  return difference <= oneMonthInMillis;
}

/**
 * Get genres and their counts from ratings
 */
export function getGenres(ratings: any[]) {
  let genres = ratings.reduce((prev, current) => {
    current.genres.forEach((g: any) => {
      prev[g.name] = (prev[g.name] || 0) + 1;
    });
    return prev;
  }, {});

  genres = Object.entries(genres)
    .sort(([, valueA]: any, [, valueB]: any) => valueB - valueA)
    .map(([key, value]) => ({ [key]: value }));

  return genres;
}

/**
 * Calculate critics wrong - movies where user rating differs significantly from critics
 */
export function getCriticsWrong(ratings: any[], userId: number) {
  return ratings
    .sort((a, b) => {
      const aRating = (getRating(a, userId) * 100) / 5;
      const bRating = (getRating(b, userId) * 100) / 5;
      a.DiffC = aRating - a.average_rating;
      b.DiffC = bRating - b.average_rating;
      return bRating - b.average_rating - (aRating - a.average_rating);
    })
    .filter((m) => m.DiffC >= 20)
    .slice(0, 3);
}

/**
 * Calculate users wrong - movies where user rating differs from other users
 */
export function getUsersWrong(ratings: any[], userId: number) {
  return ratings
    .sort((a, b) => {
      const aRating = getRating(a, userId);
      const bRating = getRating(b, userId);
      a.Diff = aRating - a.user_average_rating;
      b.Diff = bRating - b.user_average_rating;
      return (
        bRating - b.user_average_rating - (aRating - a.user_average_rating)
      );
    })
    .filter((m) => m.Diff >= 2)
    .slice(0, 3);
}

/**
 * Compare movies by user rating
 */
export function compareByUserRating(a: any, b: any, userId: number) {
  const aRating = getRating(a, userId);
  const bRating = getRating(b, userId);
  return bRating - aRating;
}

/**
 * Calculate watched duration in minutes
 */
export function getWatchedDuration(movies: any[]): number {
  return movies.reduce((t, m) => t + m.runtime, 0);
}

/**
 * Get top N movies by user rating
 */
export function getTopMovies(
  ratings: any[],
  userId: number,
  count: number = 5,
) {
  return [...ratings]
    .sort((a, b) => compareByUserRating(a, b, userId))
    .slice(0, count);
}

/**
 * Get bottom N movies by user rating
 */
export function getBottomMovies(
  ratings: any[],
  userId: number,
  count: number = 5,
) {
  return [...ratings]
    .sort((a, b) => compareByUserRating(a, b, userId))
    .slice(-count)
    .reverse();
}

/**
 * Get movies rated from watchlist
 */
export function getMoviesFromWatchlist(ratings: any[], userId: number) {
  return ratings.filter(
    (m) => getLogEntryByUserId(m, userId).watchlist_date != null,
  );
}

/**
 * Get movies rated on or near release
 */
export function getMoviesOnRelease(ratings: any[], userId: number) {
  return ratings.filter((m) =>
    isWithinOneMonth(
      getLogEntryByUserId(m, userId).rating_date,
      m.release_date,
    ),
  );
}

/**
 * Get longest movie
 */
export function getLongestMovie(ratings: any[]) {
  return ratings.reduce((prev, current) =>
    prev.runtime > current.runtime ? prev : current,
  );
}

/**
 * Get oldest movie
 */
export function getOldestMovie(ratings: any[]) {
  return ratings.reduce((prev, current) =>
    prev.release_date < current.release_date ? prev : current,
  );
}

/**
 * Calculate average rating
 */
export function getAverageRating(ratings: any[], userId: number): number {
  if (ratings.length === 0) return 0;
  return ratings.reduce((t, m) => t + getRating(m, userId), 0) / ratings.length;
}
